/**
 * The on-screen movement stick (docs/design/mobile-layout.md §7.1; `q-touch-controls-overlay`,
 * T3 of AD-817). Pure — no DOM, no THREE — so every rule is unit-tested under `node --test`.
 *
 * - **Dynamic origin:** the stick's centre is wherever the thumb lands in the stick zone,
 *   clamped so the ring stays inside the zone (`clampOrigin`).
 * - **Dead zone:** within `STICK_DEAD_ZONE_PX` of the origin the stick reads as idle — a
 *   resting thumb never walks and never cancels a walk-to (it is not "steering").
 * - **Clamp:** the knob travels at most `STICK_MAX_RADIUS_PX`; the finger may go further.
 * - **Direction only:** past the dead zone the reading is a UNIT direction in stick space
 *   (`ix` right, `iz` forward = screen-up), fed through the same camera-relative mapping as
 *   WASD (`cameraRelativeMove` in `../input.ts`) — full speed, exactly as a key gives.
 * - **One owning pointer:** the first finger down owns the stick; other pointers are ignored
 *   until it lifts (multi-touch never hijacks the stick).
 */

/**
 * Knob travel inside which the stick is idle, CSS px. 12 px sits just above `TAP_SLOP_PX`
 * (10 px — finger jitter on a deliberate tap), so a resting thumb never walks.
 */
export const STICK_DEAD_ZONE_PX = 12;

/** Furthest the knob travels from the origin, CSS px (a 112 px ring — a thumb's easy sweep). */
export const STICK_MAX_RADIUS_PX = 56;

export interface Point {
  x: number;
  y: number;
}

/** A zone rectangle in the same px space as the pointer samples. */
export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface StickReading {
  /** Past the dead zone: the player is steering with the stick. */
  active: boolean;
  /** Unit direction in stick space: +x = screen-right, +z = screen-up (forward). 0 when idle. */
  ix: number;
  iz: number;
  /** The knob's offset from the origin, clamped to `STICK_MAX_RADIUS_PX` (screen px, +y down). */
  knobX: number;
  knobY: number;
}

export const IDLE_READING: StickReading = Object.freeze({
  active: false,
  ix: 0,
  iz: 0,
  knobX: 0,
  knobY: 0,
});

/** Read the stick for a finger at `point` with the stick's origin at `origin` (screen px). */
export function stickReading(
  origin: Point,
  point: Point,
  deadZone: number = STICK_DEAD_ZONE_PX,
  maxRadius: number = STICK_MAX_RADIUS_PX,
): StickReading {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  const dist = Math.hypot(dx, dy);
  if (!Number.isFinite(dist) || dist === 0) return IDLE_READING;
  const scale = dist > maxRadius ? maxRadius / dist : 1;
  const knobX = dx * scale;
  const knobY = dy * scale;
  if (dist <= deadZone) return { active: false, ix: 0, iz: 0, knobX, knobY };
  // Screen y grows downward; forward (W) is screen-up.
  return { active: true, ix: dx / dist, iz: -dy / dist, knobX, knobY };
}

/**
 * Where the stick's origin goes for a thumb landing at `point` in `zone`: the point itself,
 * moved inward so a ring of `radius` stays inside the zone. A zone narrower than the ring
 * centres it on that axis.
 */
export function clampOrigin(point: Point, zone: Rect, radius: number = STICK_MAX_RADIUS_PX): Point {
  const axis = (v: number, lo: number, size: number): number => {
    if (size <= 2 * radius) return lo + size / 2;
    return Math.min(Math.max(v, lo + radius), lo + size - radius);
  };
  return { x: axis(point.x, zone.left, zone.width), y: axis(point.y, zone.top, zone.height) };
}

/** The stick's state machine: one owning pointer, dynamic origin, reading per move. */
export class VirtualStick {
  private pointerId: number | null = null;
  private originPt: Point = { x: 0, y: 0 };
  private current: StickReading = IDLE_READING;

  /** The owning pointer's id, or null when no finger is on the stick. */
  get owner(): number | null {
    return this.pointerId;
  }

  /** The stick's origin (meaningful while `owner` is set). */
  get origin(): Point {
    return this.originPt;
  }

  get reading(): StickReading {
    return this.current;
  }

  /** A finger landed in the zone. Returns whether the stick took it (false: already held). */
  down(id: number, point: Point, zone: Rect): boolean {
    if (this.pointerId !== null) return false;
    this.pointerId = id;
    this.originPt = clampOrigin(point, zone);
    this.current = stickReading(this.originPt, point);
    return true;
  }

  /** The owning finger moved; other pointers are ignored. Returns the new reading. */
  move(id: number, point: Point): StickReading {
    if (id !== this.pointerId) return this.current;
    this.current = stickReading(this.originPt, point);
    return this.current;
  }

  /** The owning finger lifted (or was cancelled): idle. Other pointers are ignored. */
  up(id: number): boolean {
    if (id !== this.pointerId) return false;
    this.reset();
    return true;
  }

  /** Drop everything (blur, unmount, setting switched off). */
  reset(): void {
    this.pointerId = null;
    this.current = IDLE_READING;
  }
}
